import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  type Firestore,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { UNTANGLED_BLOG_PATHWAYS } from '@/lib/content/untangled-blog';
import { books } from '@/lib/data';
import { getStorageClient } from '@/lib/firebase/client';
import { COLLECTIONS } from '@/lib/firebase/paths';
import { repairTextArtifactsInValue } from '@/lib/text-encoding';
import { slugify, stripUndefined, uniqueStrings } from '@/lib/utils';
import type { PlatformBlogContentBlockDoc, PlatformBlogContentImageDoc, PlatformBlogPostDoc } from '@/types/platform';

type TimestampLike = { toMillis?: () => number; seconds?: number };
type BlogPostWriteInput = {
  ownerUid: string;
  title: string;
  category?: string;
  metaDescription?: string;
  excerpt: string;
  body: string;
  publishedDate?: string;
  readingMinutes?: number;
  likeCount?: number;
  saveCount?: number;
  shareCount?: number;
  showDropCap?: boolean;
  dropCapSectionIndex?: number;
  pullQuote?: string;
  pullQuoteAttribution?: string;
  accentCallout?: string;
  accentCalloutLabel?: string;
  accentCalloutTone?: PlatformBlogPostDoc['accentCalloutTone'];
  closingCallout?: string;
  slug?: string;
  coverImageId?: string;
  coverImageUrl?: string;
  coverImageAlt?: string;
  coverStoragePath?: string;
  contentImages?: PlatformBlogContentImageDoc[];
  contentBlocks?: PlatformBlogContentBlockDoc[];
  authorProfileId?: string | null;
  authorName?: string;
  authorTitle?: string;
  authorBio?: string;
  authorAvatarUrl?: string;
  authorAvatarAlt?: string;
  authorAvatarStoragePath?: string;
  authorProfileUrl?: string;
  relatedTitleId?: string | null;
  relatedBookSlug?: string | null;
  tags?: string[];
  isFeatured?: boolean;
  isPublic?: boolean;
  publicationStatus?: PlatformBlogPostDoc['publicationStatus'];
};

const BLOG_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const BLOG_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);

function timestampToMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object' && value) {
    const timestamp = value as TimestampLike;
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  }
  return 0;
}

function sortByRecent<T extends { publishedAt?: unknown; updatedAt?: unknown; createdAt?: unknown }>(items: T[]) {
  return [...items].sort((a, b) => {
    const aValue = timestampToMillis(a.publishedAt ?? a.updatedAt ?? a.createdAt);
    const bValue = timestampToMillis(b.publishedAt ?? b.updatedAt ?? b.createdAt);
    return bValue - aValue;
  });
}

function normalizeBlogCategory(value?: string | null, tags: string[] = [], relatedBookSlug?: string | null) {
  const clean = String(value || '').trim();
  if (clean) return clean.slice(0, 80);
  const pathwayTag = tags.find((tag) => UNTANGLED_BLOG_PATHWAYS.some((pathway) => pathway.id === tag || pathway.label === tag));
  if (pathwayTag) {
    return UNTANGLED_BLOG_PATHWAYS.find((pathway) => pathway.id === pathwayTag || pathway.label === pathwayTag)?.label ?? pathwayTag;
  }
  const relatedBook = books.find((book) => book.slug === relatedBookSlug);
  return relatedBook?.category ?? 'Editorial';
}

function normalizeMetaDescription(value?: string | null, excerpt?: string | null) {
  const clean = String(value || excerpt || '').replace(/\s+/g, ' ').trim();
  return clean.slice(0, 160);
}

function normalizePublishedDate(value?: unknown) {
  const clean = String(value || '').trim();
  if (!clean) return undefined;
  const parsed = Date.parse(clean);
  if (!Number.isFinite(parsed)) return clean.slice(0, 40);
  return new Date(parsed).toISOString().slice(0, 10);
}

function normalizeCount(value?: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.max(0, Math.min(9999999, Math.round(parsed)));
}

function normalizeContentImages(input: unknown, post?: Partial<Pick<PlatformBlogPostDoc, 'slug' | 'title' | 'coverImageId' | 'coverImageUrl' | 'coverImageAlt'>>): PlatformBlogContentImageDoc[] {
  const source = Array.isArray(input) ? input : [];
  const normalized = source
    .map((item, index) => {
      const record = item && typeof item === 'object' ? item as Partial<PlatformBlogContentImageDoc> : {};
      const layout = record.layout === 'left-text' || record.layout === 'right-text' || record.layout === 'full-width' ? record.layout : index === 0 ? 'full-width' : 'left-text';
      const imageUrl = String(record.imageUrl || '').trim();
      const imageId = String(record.imageId || '').trim();
      const imageAlt = String(record.imageAlt || '').trim();
      if (!imageUrl) return null;
      return stripUndefined({
        imageId: imageId || undefined,
        imageUrl: imageUrl || undefined,
        imageAlt: (imageAlt || `${post?.title || 'Blog'} editorial image`).slice(0, 180),
        storagePath: String(record.storagePath || '').trim() || undefined,
        caption: String(record.caption || '').trim().slice(0, 220) || undefined,
        layout,
      }) as PlatformBlogContentImageDoc;
    })
    .filter(Boolean) as PlatformBlogContentImageDoc[];
  return normalized.slice(0, 6);
}

function normalizeContentBlocks(input: unknown): PlatformBlogContentBlockDoc[] {
  const source = Array.isArray(input) ? input : [];
  return source
    .map((item, index) => {
      const record = item && typeof item === 'object' ? item as Partial<PlatformBlogContentBlockDoc> : {};
      const type = record.type === 'quote' || record.type === 'image' || record.type === 'callout' ? record.type : 'text';
      const blockId = String(record.blockId || '').trim() || `block-${index + 1}`;
      const heading = String(record.heading || '').trim().slice(0, 180);
      const text = String(record.text || '').trim();
      const quote = String(record.quote || '').trim();
      const attribution = String(record.attribution || '').trim().slice(0, 160);
      const imageUrl = String(record.imageUrl || '').trim();
      const imageAlt = String(record.imageAlt || '').trim().slice(0, 180);
      const imageStoragePath = String(record.imageStoragePath || '').trim();
      const caption = String(record.caption || '').trim().slice(0, 240);
      const tone = record.tone === 'green' || record.tone === 'rose' ? record.tone : 'cream';
      const hasContent =
        (type === 'text' && (heading || text)) ||
        (type === 'quote' && (quote || text)) ||
        (type === 'image' && imageUrl) ||
        (type === 'callout' && text);

      if (!hasContent) return null;

      return stripUndefined({
        blockId,
        type,
        heading: heading || undefined,
        text: text || undefined,
        quote: quote || undefined,
        attribution: attribution || undefined,
        imageUrl: imageUrl || undefined,
        imageAlt: imageAlt || undefined,
        imageStoragePath: imageStoragePath || undefined,
        caption: caption || undefined,
        tone,
        dropCap: Boolean(record.dropCap),
      }) as PlatformBlogContentBlockDoc;
    })
    .filter(Boolean)
    .slice(0, 80) as PlatformBlogContentBlockDoc[];
}

function createFallbackContentBlocks(post: PlatformBlogPostDoc): PlatformBlogContentBlockDoc[] {
  const sections = String(post.body || '').split(/\n{2,}/).map((item) => item.trim()).filter(Boolean);
  const images = normalizeContentImages(post.contentImages, post).filter((image) => String(image.imageUrl || '').trim());
  const fullWidthImage = images.find((image) => image.layout === 'full-width') ?? null;
  const featureInsertIndex = sections.length > 1 ? 1 : 0;
  const imageInsertIndex = sections.length > 2 ? 2 : featureInsertIndex;
  const dropCapIndex = Math.max(0, normalizeCount(post.dropCapSectionIndex) ?? 0);
  const blocks: PlatformBlogContentBlockDoc[] = [];

  (sections.length ? sections : [post.excerpt || '']).forEach((section, index) => {
    if (section.trim()) {
      blocks.push(stripUndefined({
        blockId: `legacy-text-${index + 1}`,
        type: 'text',
        text: section,
        tone: 'cream',
        dropCap: post.showDropCap !== false && index === dropCapIndex,
      }) as PlatformBlogContentBlockDoc);
    }
    if (index === featureInsertIndex && post.pullQuote?.trim()) {
      blocks.push(stripUndefined({
        blockId: 'legacy-quote',
        type: 'quote',
        quote: post.pullQuote.trim(),
        attribution: String(post.pullQuoteAttribution || '').trim() || undefined,
        tone: 'cream',
      }) as PlatformBlogContentBlockDoc);
    }
    if (index === featureInsertIndex && post.accentCallout?.trim()) {
      blocks.push(stripUndefined({
        blockId: 'legacy-accent-callout',
        type: 'callout',
        text: post.accentCallout.trim(),
        tone: post.accentCalloutTone === 'green' || post.accentCalloutTone === 'rose' ? post.accentCalloutTone : 'cream',
      }) as PlatformBlogContentBlockDoc);
    }
    if (index === imageInsertIndex && fullWidthImage) {
      blocks.push(stripUndefined({
        blockId: 'legacy-full-image',
        type: 'image',
        imageUrl: fullWidthImage.imageUrl,
        imageAlt: fullWidthImage.imageAlt,
        imageStoragePath: fullWidthImage.storagePath,
        caption: fullWidthImage.caption,
        tone: 'cream',
      }) as PlatformBlogContentBlockDoc);
    }
  });

  if (post.closingCallout?.trim()) {
    blocks.push(stripUndefined({
      blockId: 'legacy-closing-callout',
      type: 'callout',
      text: post.closingCallout.trim(),
      tone: 'cream',
    }) as PlatformBlogContentBlockDoc);
  }

  return blocks.slice(0, 80);
}

function hydratePost(rawRecord: Partial<PlatformBlogPostDoc>, id: string): PlatformBlogPostDoc {
  const record = repairTextArtifactsInValue(rawRecord) as Partial<PlatformBlogPostDoc>;
  const tags = Array.isArray(record.tags) ? record.tags : [];
  const post = record as PlatformBlogPostDoc;
  const contentBlocks = normalizeContentBlocks(post.contentBlocks);
  return {
    ...post,
    postId: record.postId ?? id,
    category: normalizeBlogCategory(post.category, tags, post.relatedBookSlug),
    metaDescription: normalizeMetaDescription(post.metaDescription, post.excerpt),
    excerpt: String(post.excerpt || '').trim(),
    body: String(post.body || '').trim(),
    publishedDate: normalizePublishedDate(post.publishedDate),
    readingMinutes: normalizeCount(post.readingMinutes),
    likeCount: normalizeCount(post.likeCount) ?? 0,
    saveCount: normalizeCount(post.saveCount) ?? 0,
    shareCount: normalizeCount(post.shareCount) ?? 0,
    showDropCap: record.showDropCap !== false,
    dropCapSectionIndex: normalizeCount(post.dropCapSectionIndex) ?? 0,
    pullQuote: String(post.pullQuote || '').trim(),
    pullQuoteAttribution: String(post.pullQuoteAttribution || '').trim(),
    accentCallout: String(post.accentCallout || '').trim(),
    accentCalloutLabel: String(post.accentCalloutLabel || '').trim(),
    accentCalloutTone: post.accentCalloutTone === 'green' || post.accentCalloutTone === 'rose' || post.accentCalloutTone === 'cream' ? post.accentCalloutTone : 'cream',
    closingCallout: String(post.closingCallout || '').trim(),
    authorTitle: String(post.authorTitle || '').trim(),
    authorBio: String(post.authorBio || '').trim(),
    authorAvatarUrl: String(post.authorAvatarUrl || '').trim(),
    authorAvatarAlt: String(post.authorAvatarAlt || '').trim(),
    authorAvatarStoragePath: String(post.authorAvatarStoragePath || '').trim(),
    authorProfileUrl: String(post.authorProfileUrl || '').trim(),
    authorProfileId: String(post.authorProfileId || '').trim() || null,
    tags,
    isFeatured: Boolean(record.isFeatured),
    isPublic: Boolean(record.isPublic),
    contentImages: normalizeContentImages(post.contentImages, post),
    contentBlocks: contentBlocks.length ? contentBlocks : createFallbackContentBlocks(post),
    publicationStatus: record.publicationStatus ?? 'draft',
  };
}

export async function listPublicBlogPosts(db: Firestore) {
  const snapshot = await getDocs(query(
    collection(db, COLLECTIONS.blogPosts),
    where('isPublic', '==', true),
    where('publicationStatus', '==', 'published'),
  ));
  const livePosts = snapshot.docs.map((item) => hydratePost(item.data(), item.id));
  return sortByRecent(livePosts);
}

export async function listAllBlogPosts(db: Firestore) {
  const snapshot = await getDocs(collection(db, COLLECTIONS.blogPosts));
  return sortByRecent(snapshot.docs.map((item) => hydratePost(item.data(), item.id)));
}

// Cursor pagination for the admin blog list, ordered by the immutable createdAt field so the
// admin never has to read the whole collection at once. Returns the last snapshot as the cursor.
export async function listAdminBlogPostsPage(
  db: Firestore,
  options: { pageSize?: number; cursor?: QueryDocumentSnapshot | null } = {},
) {
  const size = Math.min(200, Math.max(1, options.pageSize ?? 100));
  const constraints = [
    orderBy('createdAt', 'desc'),
    ...(options.cursor ? [startAfter(options.cursor)] : []),
    limit(size),
  ];
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.blogPosts), ...constraints));
  const posts = snapshot.docs.map((item) => hydratePost(item.data(), item.id));
  const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;
  return {
    posts,
    // Only expose a cursor when the page was full — otherwise we've reached the end.
    cursor: snapshot.docs.length === size ? lastDoc : null,
  };
}

export async function getBlogPostBySlug(db: Firestore, slug: string) {
  const snapshot = await getDocs(query(
    collection(db, COLLECTIONS.blogPosts),
    where('slug', '==', slug),
    where('isPublic', '==', true),
    where('publicationStatus', '==', 'published'),
    limit(1),
  ));
  const match = snapshot.docs[0];
  return match ? hydratePost(match.data(), match.id) : null;
}

async function assertBlogSlugAvailable(db: Firestore, slug: string, currentPostId?: string) {
  if (!slug) throw new Error('Blog slug is required.');
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.blogPosts), where('slug', '==', slug)));
  const conflict = snapshot.docs.find((item) => item.id !== currentPostId && item.data().postId !== currentPostId);
  if (conflict) throw new Error(`The blog slug "${slug}" is already used by another post.`);
}

export async function createBlogPost(db: Firestore, input: BlogPostWriteInput) {
  const shouldPublish = input.publicationStatus === 'published' || Boolean(input.isPublic);
  const cleanSlug = slugify(input.slug?.trim() || input.title);
  await assertBlogSlugAvailable(db, cleanSlug);
  const tags = uniqueStrings(input.tags ?? []);
  const payload = stripUndefined({
    ownerUid: input.ownerUid,
    slug: cleanSlug,
    title: input.title.trim(),
    category: normalizeBlogCategory(input.category, tags, input.relatedBookSlug),
    metaDescription: normalizeMetaDescription(input.metaDescription, input.excerpt),
    excerpt: input.excerpt.trim(),
    body: input.body.trim(),
    publishedDate: normalizePublishedDate(input.publishedDate),
    readingMinutes: normalizeCount(input.readingMinutes),
    likeCount: normalizeCount(input.likeCount),
    saveCount: normalizeCount(input.saveCount),
    shareCount: normalizeCount(input.shareCount),
    showDropCap: input.showDropCap !== false,
    dropCapSectionIndex: normalizeCount(input.dropCapSectionIndex),
    pullQuote: input.pullQuote?.trim() || undefined,
    pullQuoteAttribution: input.pullQuoteAttribution?.trim() || undefined,
    accentCallout: input.accentCallout?.trim() || undefined,
    accentCalloutLabel: input.accentCalloutLabel?.trim() || undefined,
    accentCalloutTone: input.accentCalloutTone || undefined,
    closingCallout: input.closingCallout?.trim() || undefined,
    coverImageId: input.coverImageId?.trim() || undefined,
    coverImageUrl: input.coverImageUrl?.trim() || undefined,
    coverImageAlt: input.coverImageAlt?.trim() || undefined,
    coverStoragePath: input.coverStoragePath?.trim() || undefined,
    contentImages: normalizeContentImages(input.contentImages, { slug: cleanSlug, title: input.title, coverImageId: input.coverImageId, coverImageUrl: input.coverImageUrl, coverImageAlt: input.coverImageAlt }),
    authorName: input.authorName?.trim() || undefined,
    authorTitle: input.authorTitle?.trim() || undefined,
    authorBio: input.authorBio?.trim() || undefined,
    authorAvatarUrl: input.authorAvatarUrl?.trim() || undefined,
    authorAvatarAlt: input.authorAvatarAlt?.trim() || undefined,
    authorAvatarStoragePath: input.authorAvatarStoragePath?.trim() || undefined,
    authorProfileUrl: input.authorProfileUrl?.trim() || undefined,
    authorProfileId: input.authorProfileId?.trim() || null,
    contentBlocks: normalizeContentBlocks(input.contentBlocks),
    relatedTitleId: input.relatedTitleId || undefined,
    relatedBookSlug: input.relatedBookSlug || undefined,
    tags,
    isFeatured: Boolean(input.isFeatured),
    isPublic: shouldPublish,
    publicationStatus: shouldPublish ? 'published' : 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: shouldPublish ? serverTimestamp() : undefined,
  });
  const ref = await addDoc(collection(db, COLLECTIONS.blogPosts), payload);
  await updateDoc(ref, { postId: ref.id });
  return ref.id;
}

export async function updateBlogPost(db: Firestore, postId: string, patch: Partial<PlatformBlogPostDoc>) {
  const shouldPublish = patch.publicationStatus === 'published' || patch.isPublic === true;
  const cleanSlug = typeof patch.slug === 'string' ? slugify(patch.slug) : undefined;
  if (cleanSlug) await assertBlogSlugAvailable(db, cleanSlug, postId);
  const tags = patch.tags ? uniqueStrings(patch.tags) : undefined;
  const base = stripUndefined({
    ...patch,
    slug: cleanSlug,
    category: patch.category !== undefined ? normalizeBlogCategory(patch.category, tags ?? [], patch.relatedBookSlug) : undefined,
    metaDescription: patch.metaDescription !== undefined || patch.excerpt !== undefined ? normalizeMetaDescription(patch.metaDescription, patch.excerpt) : undefined,
    publishedDate: patch.publishedDate !== undefined ? normalizePublishedDate(patch.publishedDate) : undefined,
    readingMinutes: patch.readingMinutes !== undefined ? normalizeCount(patch.readingMinutes) : undefined,
    likeCount: patch.likeCount !== undefined ? normalizeCount(patch.likeCount) : undefined,
    saveCount: patch.saveCount !== undefined ? normalizeCount(patch.saveCount) : undefined,
    shareCount: patch.shareCount !== undefined ? normalizeCount(patch.shareCount) : undefined,
    showDropCap: patch.showDropCap === undefined ? undefined : Boolean(patch.showDropCap),
    dropCapSectionIndex: patch.dropCapSectionIndex !== undefined ? normalizeCount(patch.dropCapSectionIndex) : undefined,
    pullQuote: patch.pullQuote !== undefined ? String(patch.pullQuote || '').trim() : undefined,
    pullQuoteAttribution: patch.pullQuoteAttribution !== undefined ? String(patch.pullQuoteAttribution || '').trim() : undefined,
    accentCallout: patch.accentCallout !== undefined ? String(patch.accentCallout || '').trim() : undefined,
    accentCalloutLabel: patch.accentCalloutLabel !== undefined ? String(patch.accentCalloutLabel || '').trim() : undefined,
    accentCalloutTone: patch.accentCalloutTone !== undefined ? patch.accentCalloutTone : undefined,
    closingCallout: patch.closingCallout !== undefined ? String(patch.closingCallout || '').trim() : undefined,
    authorTitle: patch.authorTitle !== undefined ? String(patch.authorTitle || '').trim() : undefined,
    authorBio: patch.authorBio !== undefined ? String(patch.authorBio || '').trim() : undefined,
    authorAvatarUrl: patch.authorAvatarUrl !== undefined ? String(patch.authorAvatarUrl || '').trim() : undefined,
    authorAvatarAlt: patch.authorAvatarAlt !== undefined ? String(patch.authorAvatarAlt || '').trim() : undefined,
    authorAvatarStoragePath: patch.authorAvatarStoragePath !== undefined ? String(patch.authorAvatarStoragePath || '').trim() : undefined,
    authorProfileUrl: patch.authorProfileUrl !== undefined ? String(patch.authorProfileUrl || '').trim() : undefined,
    authorProfileId: patch.authorProfileId !== undefined ? String(patch.authorProfileId || '').trim() || null : undefined,
    tags,
    contentImages: patch.contentImages ? normalizeContentImages(patch.contentImages, patch) : undefined,
    contentBlocks: patch.contentBlocks ? normalizeContentBlocks(patch.contentBlocks) : undefined,
    isPublic: patch.isPublic === undefined ? undefined : Boolean(patch.isPublic),
    publicationStatus: patch.publicationStatus ?? (shouldPublish ? 'published' : undefined),
    updatedAt: serverTimestamp(),
    publishedAt: shouldPublish ? serverTimestamp() : patch.publishedAt,
  });
  await updateDoc(doc(db, COLLECTIONS.blogPosts, postId), base);
}

export async function deleteBlogPostById(db: Firestore, postId: string) {
  await deleteDoc(doc(db, COLLECTIONS.blogPosts, postId));
}

export async function getBlogPostById(db: Firestore, postId: string) {
  const snapshot = await getDoc(doc(db, COLLECTIONS.blogPosts, postId));
  if (!snapshot.exists()) return null;
  return hydratePost(snapshot.data(), snapshot.id);
}

function assertBlogImageFile(file: File) {
  if (!BLOG_IMAGE_TYPES.has(file.type)) throw new Error('Use a JPG, PNG, WebP, or AVIF image.');
  if (file.size > BLOG_IMAGE_MAX_BYTES) throw new Error('Blog images must be 8 MB or smaller.');
}

export async function uploadBlogImage(input: { ownerUid: string; file: File; slugHint?: string; role?: 'cover' | 'content-full' | 'content-side' | 'author-avatar' }) {
  const storage = getStorageClient();
  if (!storage) throw new Error('Firebase Storage is not configured.');
  assertBlogImageFile(input.file);
  const extension = input.file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const safeSlug = slugify(input.slugHint || input.file.name || 'blog-image') || 'blog-image';
  const role = input.role || 'cover';
  const storagePath = `platform/blog/${input.ownerUid}/${role}-${safeSlug}-${Date.now()}.${extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, input.file, { contentType: input.file.type || 'image/jpeg' });
  const downloadUrl = await getDownloadURL(storageRef);
  return {
    imageUrl: downloadUrl,
    storagePath,
    coverImageUrl: downloadUrl,
    coverStoragePath: storagePath,
  };
}

export const uploadBlogCoverImage = uploadBlogImage;

export function getLegacyRelatedBook(slug?: string | null) {
  if (!slug) return null;
  return books.find((book) => book.slug === slug) ?? null;
}
